// Hook personalizado para manejar perfil de usuario con TanStack Query.
// Centraliza la lógica de obtención y actualización del perfil, manejando estados async.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { getUserProfile, updateUserProfile, updateProfilePreferences } from '../services/userProfileService';

/**
 * Hook para obtener perfil del usuario.
 * Usa query para lecturas, habilitado solo si hay sesión.
 * @param {Object} options - Opciones adicionales para la query
 * @param {boolean} options.enabled - Habilita/deshabilita la query (además de la verificación de sesión)
 */
export const useUserProfileQuery = (options = {}) => {
  const { user } = useAuth();
  const { enabled: enabledOption = true } = options;

  return useQuery({
    queryKey: ['userProfile', user?.id],
    queryFn: () => getUserProfile(user?.id),
    enabled: !!user?.id && enabledOption,
    staleTime: 5 * 60 * 1000, // 5 minutos
    onError: (error) => {
      console.error('Error al obtener perfil:', error);
    },
  });
};

// Hook para actualizar perfil del usuario.
// Usa mutation para escrituras, invalida query del perfil al éxito.
export const useUpdateUserProfileMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (profileData) => {
      // Actualiza perfil
      const updatedProfile = await updateUserProfile(user?.id, profileData);

      // Si incluye sex, actualiza preferencias
      if (profileData.sex !== undefined) {
        await updateProfilePreferences(user?.id, profileData.sex);
      }

      return updatedProfile;
    },
    onSuccess: () => {
      // Invalida query del perfil para refrescar datos
      queryClient.invalidateQueries(['userProfile', user?.id]);
      queryClient.invalidateQueries(['currentUserProfile', user?.id]);
    },
    onError: (error) => {
      console.error('Error al actualizar perfil:', error);
    },
  });
};
